import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrashIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';
import JoditEditor from 'jodit-react';
import { firebaseAuth } from '../../../providers/AuthProvider';
import { db } from '../../../firebase';
import {
  normalizeModelInterestCatalog,
  resolveModelInterestCatalog,
} from '../../../utils/modelInterest';

const inputCls =
  'mt-2 block w-full rounded-md border-0 bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 disabled:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:focus:ring-white dark:disabled:bg-zinc-700';

const cardCls = 'rounded-lg bg-zinc-50 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-zinc-700';

const Card = ({ title, action, children }) => (
  <div className={cardCls}>
    <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Settings = () => {
  const { user } = useContext(firebaseAuth);
  const navigate = useNavigate();
  const optInCopyEditor = useRef(null);
  const waiverCopyEditor = useRef(null);

  const [inputs, setInputs] = useState({
    mailboxes: [],
    waiverDefaultCopy: [],
    optInDefaultCopy: [],
    eventDataRetention: 0,
    offlineDataRetention: 0,
    modelInterestOptions: resolveModelInterestCatalog(null),
  });
  const [oldSettings, setOldSettings] = useState({});
  const [optInIndex, setOptInIndex] = useState(null);
  const [waiverIndex, setWaiverIndex] = useState(null);

  // jodit-react rebuilds the editor whenever the config object changes. Keep
  // this reference stable so a state update triggered by the paste dialog's
  // blur event does not destroy the editor before the paste is completed.
  const editorConfig = useMemo(
    () => ({ minHeight: 400, maxWidth: 800, toolbar: true, readonly: false }),
    [],
  );

  useEffect(() => {
    db.collection('settings')
      .doc('current')
      .get()
      .then((docRef) => {
        const data = docRef.data() || {};
        setInputs({
          mailboxes: [],
          waiverDefaultCopy: [],
          optInDefaultCopy: [],
          eventDataRetention: 0,
          offlineDataRetention: 0,
          ...data,
          modelInterestOptions: resolveModelInterestCatalog(
            data.modelInterestOptions,
          ),
        });
        setOldSettings({ ...data, timestamp: new Date().getUTCDate() });
      });
  }, []);

  if (!user?.isAuthenticated || user?.role > 1) {
    if (user?.isAuthenticated && user?.role > 1) navigate('/manage/permissions');
    else navigate('/manage/sign-in');
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const nextInputs = {
      ...inputs,
      modelInterestOptions: normalizeModelInterestCatalog(
        inputs.modelInterestOptions,
      ),
    };
    setInputs(nextInputs);
    db.collection('settings').doc('current').set(nextInputs);
    db.collection('settings').add(oldSettings);
  };

  const updateModelInterestOption = (index, field, value) => {
    const modelInterestOptions = [...inputs.modelInterestOptions];
    modelInterestOptions[index] = {
      ...modelInterestOptions[index],
      [field]: value,
    };
    setInputs({ ...inputs, modelInterestOptions });
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleOptInCopy = (content) => {
    if (optInIndex == null) return;
    const optInDefaultCopy = [...inputs.optInDefaultCopy];
    optInDefaultCopy[optInIndex.key] = {
      ...optInDefaultCopy[optInIndex.key],
      content,
    };
    setInputs({ ...inputs, optInDefaultCopy });
  };

  const handleWaiverCopy = (content) => {
    if (waiverIndex == null) return;
    const waiverDefaultCopy = [...inputs.waiverDefaultCopy];
    waiverDefaultCopy[waiverIndex.key] = {
      ...waiverDefaultCopy[waiverIndex.key],
      content,
    };
    setInputs({ ...inputs, waiverDefaultCopy });
  };

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">Global settings</h1>

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <Card title="Data retention">
          <label htmlFor="eventDataRetention" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Event data retention (days)
          </label>
          <input
            id="eventDataRetention"
            type="number"
            disabled
            name="eventDataRetention"
            value={inputs.eventDataRetention}
            onChange={handleChange}
            className={inputCls}
          />
        </Card>

        <Card
          title="Mailboxes"
          action={
            <button
              type="button"
              onClick={() =>
                setInputs({ ...inputs, mailboxes: [...inputs.mailboxes, ''] })
              }
              className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs font-semibold text-zinc-50 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
            >
              <PlusIcon className="size-3.5" /> Mailbox
            </button>
          }
        >
          <div className="space-y-2">
            {inputs?.mailboxes?.map((mailbox, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  className={`${inputCls} mt-0`}
                  value={mailbox}
                  onChange={(e) => {
                    const mailboxes = [...inputs.mailboxes];
                    mailboxes[index] = e.target.value;
                    setInputs({ ...inputs, mailboxes });
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const mailboxes = inputs.mailboxes.filter((_, i) => i !== index);
                    setInputs({ ...inputs, mailboxes });
                  }}
                  className="text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400"
                >
                  <TrashIcon className="size-4" />
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card
          title="Model interest"
          action={
            <button
              type="button"
              onClick={() =>
                setInputs({
                  ...inputs,
                  modelInterestOptions: [
                    ...inputs.modelInterestOptions,
                    { value: '', label: '' },
                  ],
                })
              }
              className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs font-semibold text-zinc-50 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
            >
              <PlusIcon className="size-3.5" /> Model
            </button>
          }
        >
          <p className="mb-3 text-sm text-zinc-600 dark:text-zinc-400">
            Codes saved on registrations (for example <code>id_ao3_primint</code>)
            and the model names shown in dropdowns. Blank or duplicate codes are
            dropped when you save. Leave this list empty and save to show no
            models; a never-configured catalog falls back to the built-in Porsche
            lineup.
          </p>
          <div className="space-y-2">
            {inputs.modelInterestOptions.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  aria-label={`Model interest code ${index + 1}`}
                  className={`${inputCls} mt-0`}
                  onChange={(e) =>
                    updateModelInterestOption(index, 'value', e.target.value)
                  }
                  placeholder="id_ao3_primint"
                  type="text"
                  value={option.value}
                />
                <input
                  aria-label={`Model interest name ${index + 1}`}
                  className={`${inputCls} mt-0`}
                  onChange={(e) =>
                    updateModelInterestOption(index, 'label', e.target.value)
                  }
                  placeholder="911"
                  type="text"
                  value={option.label}
                />
                <button
                  aria-label={`Remove model interest ${option.label || option.value || index + 1}`}
                  className="text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400"
                  onClick={() => {
                    const modelInterestOptions =
                      inputs.modelInterestOptions.filter((_, i) => i !== index);
                    setInputs({ ...inputs, modelInterestOptions });
                  }}
                  type="button"
                >
                  <TrashIcon className="size-4" />
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <Card
          title="Opt-in language"
          action={
            !optInIndex && (
              <button
                type="button"
                onClick={() =>
                  setInputs({
                    ...inputs,
                    optInDefaultCopy: [...inputs.optInDefaultCopy, {}],
                  })
                }
                className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs font-semibold text-zinc-50 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
              >
                <PlusIcon className="size-3.5" /> Opt-in
              </button>
            )
          }
        >
          {!optInIndex ? (
            <div className="space-y-2">
              {inputs?.optInDefaultCopy?.map((optin, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    className={`${inputCls} mt-0`}
                    value={optin?.name || ''}
                    onChange={(e) => {
                      const optInDefaultCopy = [...inputs.optInDefaultCopy];
                      optInDefaultCopy[index] = {
                        name: e.target.value,
                        content: optInDefaultCopy[index]?.content || '',
                      };
                      setInputs({ ...inputs, optInDefaultCopy });
                    }}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setOptInIndex({ key: index, content: optin.content || '' })
                    }
                    aria-label={`Edit ${optin?.name || `opt-in ${index + 1}`}`}
                    className="text-zinc-400 hover:text-zinc-900 dark:text-zinc-500 dark:hover:text-zinc-100"
                  >
                    <PencilSquareIcon className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const optInDefaultCopy = inputs.optInDefaultCopy.filter(
                        (_, i) => i !== index,
                      );
                      setInputs({ ...inputs, optInDefaultCopy });
                    }}
                    className="text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400"
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <JoditEditor
                ref={optInCopyEditor}
                value={optInIndex?.content || ''}
                config={editorConfig}
                tabIndex={1}
                onBlur={(content) => handleOptInCopy(content)}
                onChange={() => {}}
              />
              <div className="mt-3 text-right">
                <button
                  type="button"
                  onClick={() => setOptInIndex(null)}
                  className="inline-flex items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6">
        <Card
          title="Waivers"
          action={
            !waiverIndex && (
              <button
                type="button"
                onClick={() =>
                  setInputs({
                    ...inputs,
                    waiverDefaultCopy: [...inputs.waiverDefaultCopy, {}],
                  })
                }
                className="inline-flex items-center gap-1 rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs font-semibold text-zinc-50 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
              >
                <PlusIcon className="size-3.5" /> Waiver
              </button>
            )
          }
        >
          {!waiverIndex ? (
            <div className="space-y-2">
              {inputs?.waiverDefaultCopy?.map((waiver, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    className={`${inputCls} mt-0`}
                    value={waiver?.name || ''}
                    onChange={(e) => {
                      const waiverDefaultCopy = [...inputs.waiverDefaultCopy];
                      waiverDefaultCopy[index] = {
                        name: e.target.value,
                        content: waiverDefaultCopy[index]?.content || '',
                      };
                      setInputs({ ...inputs, waiverDefaultCopy });
                    }}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setWaiverIndex({ key: index, content: waiver.content || '' })
                    }
                    className="text-zinc-400 hover:text-zinc-900 dark:text-zinc-500 dark:hover:text-zinc-100"
                  >
                    <PencilSquareIcon className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const waiverDefaultCopy = inputs.waiverDefaultCopy.filter(
                        (_, i) => i !== index,
                      );
                      setInputs({ ...inputs, waiverDefaultCopy });
                    }}
                    className="text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400"
                  >
                    <TrashIcon className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <JoditEditor
                ref={waiverCopyEditor}
                value={waiverIndex?.content || ''}
                config={editorConfig}
                tabIndex={1}
                onBlur={(content) => handleWaiverCopy(content)}
                onChange={() => {}}
              />
              <div className="mt-3 text-right">
                <button
                  type="button"
                  onClick={() => setWaiverIndex(null)}
                  className="inline-flex items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          className="inline-flex items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
        >
          Save
        </button>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md bg-zinc-50 px-3 py-2 text-sm font-semibold text-zinc-900 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:hover:bg-zinc-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default Settings;
